import { useTranslation } from 'react-i18next'
import { addToast, Button, Form, Input, Spinner } from '@heroui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  PbECommerceSettingsUseCaseEnum,
  PbPortalSettingsIdentificationFlowEnum
} from '@itsrever/rever-api'
import { useForm, useWatch } from 'react-hook-form'
import { createSearchParams, useNavigate, useSearchParams } from 'react-router'
import { z } from 'zod'
import { AxiosError } from 'axios'
import { usePostHog } from '@posthog/react'
import { useTheme } from '@/ui/theme'
import clsx from 'clsx'
import { useTranslate } from '@/hooks/useTranslate'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { parseUrlEmail } from '@/utils'
import { useAuthenticateOrder } from '@/hooks/use-authenticate-order'
import { usePages, useSlug } from '@/hooks'
import { setTagsSentry, setUserSentry } from '@/services'
import { setOrder } from '@/redux/api/returns-api-slice'
import {
  setEmail,
  setOrderNumber,
  setOrderProducts
} from '@/redux/order/order-slice'
import {
  setExchangeAddress,
  setReturnAddress
} from '@/redux/return/return-slice'
import { redirectPath } from '@/utils/redirect'
import { resetStore } from '@/redux/store'

export const testOrderID = 'N1234'

const baseSchema = z.object({
  orderId: z
    .string()
    .min(1, { message: 'landing_order_number_required_error' })
    .trim()
})

const schema = z.discriminatedUnion('identificationFlow', [
  baseSchema.extend({
    identificationFlow: z.literal(
      PbPortalSettingsIdentificationFlowEnum.OrderIdAndEmail
    ),
    email: z.email({ message: 'landing_email_error' })
  }),
  baseSchema.extend({
    identificationFlow: z.literal(
      PbPortalSettingsIdentificationFlowEnum.OrderIdAndPostalCode
    ),
    postalCode: z.string().min(1, { message: 'landing_postal_code_error' })
  }),
  baseSchema.extend({
    identificationFlow: z.literal(
      PbPortalSettingsIdentificationFlowEnum.IdentificationFlowUnspecified
    ),
    email: z.email({ message: 'landing_email_error' })
  })
])

type LoginData = z.infer<typeof schema>

export const LoginForm = () => {
  const { i18n, t } = useTranslation()
  const { translate } = useTranslate()

  const posthog = usePostHog()

  const dispatch = useAppDispatch()

  const theme = useTheme()

  const navigate = useNavigate()
  const { pages } = usePages()
  const [searchParams] = useSearchParams()

  const slug = useSlug()

  const emailParam = searchParams.get('email')
  const orderNumberParam = searchParams.get('orderNumber')
  const postalCodeParam = searchParams.get('postalCode')

  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )

  const { mutate, isPending } = useAuthenticateOrder()

  const {
    register,
    formState: { errors },
    control,
    setError,
    handleSubmit
  } = useForm<LoginData>({
    resolver: zodResolver(schema),
    defaultValues: {
      identificationFlow: settings.identification_flow!,
      orderId: orderNumberParam ?? '',
      email: emailParam ? parseUrlEmail(emailParam) : '',
      postalCode: postalCodeParam ?? ''
    },
    mode: 'onChange'
  })

  const identificationFlow = useWatch({ control, name: 'identificationFlow' })

  const handleLogin = (values: LoginData) => {
    const { orderId } = values

    const customerPrintedOrderId =
      orderId === 'REVER123' ? testOrderID : orderId

    let ecommerceId = settings.ecommerce_id

    if (ecommerceId) {
      if (orderId === 'REVER123') {
        ecommerceId = 'partner123'
      }

      const redirectFrom =
        redirectPath[ecommerceId as keyof typeof redirectPath & string]

      if (redirectFrom) {
        const orderPrefix = customerPrintedOrderId.substring(0, 5).toUpperCase()

        const portalPath =
          redirectFrom[orderPrefix as keyof typeof redirectFrom & string]

        if (portalPath) {
          ecommerceId = portalPath // for this cases the slug is the same as the ecommerce id
        }
      }

      const isReverLite =
        settings.use_case === PbECommerceSettingsUseCaseEnum.Lite

      if (isReverLite) {
        const email =
          values.identificationFlow !==
          PbPortalSettingsIdentificationFlowEnum.OrderIdAndPostalCode
            ? values.email
            : undefined

        dispatch(setOrderNumber(customerPrintedOrderId))
        dispatch(setEmail(email))

        posthog.identify(`${email}-${ecommerceId}`, {
          email,
          ecommerceId
        })

        posthog.register({
          is_testing_user: customerPrintedOrderId === testOrderID
        })

        posthog.capture('Started', {
          orderNumber: customerPrintedOrderId
        })

        setUserSentry(email)
        setTagsSentry({
          ecommerceID: ecommerceId,
          orderNumber: customerPrintedOrderId,
          email: email ?? '',
          portalURL: `https://returns.byrever.com/${slug}?orderNumber=${encodeURIComponent(customerPrintedOrderId ?? '')}&email=${encodeURIComponent(email ?? '')}`
        })

        navigate(pages.ItemSelection)
      } else {
        mutate(
          {
            customerPrintedOrderId,
            ...(values.identificationFlow ===
            PbPortalSettingsIdentificationFlowEnum.OrderIdAndPostalCode
              ? { postalCode: values.postalCode }
              : { email: values.email }),
            ecommerceId,
            userPreferredLang: i18n.language
          },
          {
            onSuccess: (response) => {
              const { order, previous_return_processes } = response

              dispatch(resetStore())

              dispatch(
                setOrderNumber(order?.identification?.customer_printed_order_id)
              )
              dispatch(setEmail(order?.customer?.email))
              dispatch(setOrder(response))
              dispatch(setOrderProducts(order?.line_items))

              const address = order?.shipping_address
              if (address) {
                dispatch(setReturnAddress(address))
                dispatch(setExchangeAddress(address))
              }

              posthog.identify(
                `${order?.customer?.email}-${ecommerceId}-${customerPrintedOrderId}`,
                {
                  email: order?.customer?.email,
                  ecommerceId: settings.ecommerce_id,
                  orderNumber: customerPrintedOrderId,
                  first_name: order?.customer?.first_name,
                  last_name: order?.customer?.last_name
                }
              )

              posthog.register({
                is_testing_user:
                  order?.identification?.customer_printed_order_id ===
                  testOrderID
              })

              posthog.capture('Started', {
                orderNumber: customerPrintedOrderId,
                checkout_id: response.checkout_id
              })

              setUserSentry(order?.customer?.email)
              setTagsSentry({
                ecommerceID: ecommerceId as string,
                orderNumber: customerPrintedOrderId,
                email: order?.customer?.email ?? '',
                portalURL: `https://returns.byrever.com/${
                  settings.slug
                }?orderNumber=${encodeURIComponent(
                  customerPrintedOrderId
                )}&email=${encodeURIComponent(order?.customer?.email ?? '')}`
              })

              const isRedirectPortal =
                redirectPath[slug as keyof typeof redirectPath & string]

              const shouldRedirect =
                slug && ecommerceId !== 'partner123' && isRedirectPortal

              const hasPreviousReturns =
                response.previous_return_processes &&
                response.previous_return_processes.length > 0

              if (shouldRedirect) {
                let path = `/${ecommerceId}/order`
                if (hasPreviousReturns) {
                  path = `/${ecommerceId}/select`
                }
                navigate({
                  pathname: path,
                  search: createSearchParams({
                    orderNumber: customerPrintedOrderId,
                    email: order?.customer?.email ?? ''
                  }).toString()
                })
                return
              }

              if (
                previous_return_processes &&
                previous_return_processes.length > 0
              ) {
                navigate({
                  pathname: pages.ProcessSelection,
                  search: createSearchParams({
                    orderNumber: customerPrintedOrderId,
                    email: order?.customer?.email ?? ''
                  }).toString()
                })

                return
              }

              navigate({
                pathname: pages.ItemSelection,
                search: createSearchParams({
                  orderNumber: customerPrintedOrderId,
                  email: order?.customer?.email ?? ''
                }).toString()
              })
            },
            onError: (error) => {
              if (error instanceof AxiosError) {
                if (error.response?.status === 404) {
                  setError('orderId', {
                    message:
                      values.identificationFlow ===
                      PbPortalSettingsIdentificationFlowEnum.OrderIdAndPostalCode
                        ? 'landing_order_number_error_postal_code'
                        : 'landing_order_number_error'
                  })

                  return
                }

                if (error.response?.status === 503) {
                  addToast({
                    title: t('toast_errors.something_went_wrong'),
                    description: t('toast_errors.confirmation_503')
                  })

                  return
                }
              }

              addToast({
                title: t('toast_errors.something_went_wrong'),
                description: t('toast_errors.error_406')
              })
            }
          }
        )
      }
    }
  }

  const emailError = 'email' in errors ? errors.email : undefined
  const postalCodeError = 'postalCode' in errors ? errors.postalCode : undefined

  const exampleEmail = `${translate('landing_email_example')}@${
    settings.slug
  }.com`

  const color = theme.colors.primary.main
  const textColor = theme.colors.primary.contrastText
  const rounded = theme.borderRadius

  const isHoff = settings.ecommerce_group == 'hoff'
  const isMisako = settings.ecommerce_group == 'misako'

  if (isHoff) {
    return (
      <Form
        className="w-full space-y-2 md:w-[440px]"
        onSubmit={handleSubmit(handleLogin)}
      >
        <Input
          {...register('orderId')}
          type="text"
          placeholder="X01234"
          radius="sm"
          variant="underlined"
          autoFocus
          isInvalid={!!errors.orderId}
          errorMessage={
            errors.orderId?.message
              ? translate(errors.orderId?.message)
              : undefined
          }
          data-testid="orderNumber"
          classNames={{
            inputWrapper: 'border-solid'
          }}
        />
        {identificationFlow ===
        PbPortalSettingsIdentificationFlowEnum.OrderIdAndPostalCode ? (
          <Input
            {...register('postalCode')}
            placeholder="12345"
            autoComplete="postal-code"
            radius="sm"
            size="md"
            variant="underlined"
            isInvalid={!!postalCodeError}
            errorMessage={
              postalCodeError?.message
                ? translate(postalCodeError?.message)
                : undefined
            }
            data-testid="postal-code"
            classNames={{
              inputWrapper: 'border-solid'
            }}
          />
        ) : (
          <Input
            {...register('email')}
            type="email"
            autoComplete="email"
            placeholder={exampleEmail}
            radius="sm"
            size="md"
            variant="underlined"
            isInvalid={!!emailError}
            errorMessage={
              emailError?.message ? translate(emailError?.message) : undefined
            }
            data-testid="email"
            classNames={{
              inputWrapper: 'border-solid'
            }}
          />
        )}
        <Button
          type="submit"
          variant="solid"
          radius="sm"
          size="lg"
          fullWidth
          data-testid="continue"
          style={{
            backgroundColor: color,
            color: textColor,
            borderRadius: '0.5rem'
          }}
          isLoading={isPending}
          spinner={<Spinner size="sm" />}
        >
          <p className="font-light">{translate('landing_button_continue')}</p>
        </Button>
      </Form>
    )
  }

  if (isMisako) {
    return (
      <Form
        className="w-full max-w-[33rem] space-y-2"
        onSubmit={handleSubmit(handleLogin)}
      >
        <Input
          {...register('orderId')}
          type="text"
          label={translate('landing_numorder')}
          radius="sm"
          variant="bordered"
          autoFocus
          isInvalid={!!errors.orderId}
          errorMessage={
            errors.orderId?.message
              ? translate(errors.orderId?.message)
              : undefined
          }
          data-testid="orderNumber"
          classNames={{
            inputWrapper: 'border-solid'
          }}
        />
        {identificationFlow ===
        PbPortalSettingsIdentificationFlowEnum.OrderIdAndPostalCode ? (
          <Input
            {...register('postalCode')}
            label={translate('landing_postal_code')}
            autoComplete="postal-code"
            radius="sm"
            size="md"
            variant="bordered"
            isInvalid={!!postalCodeError}
            errorMessage={
              postalCodeError?.message
                ? translate(postalCodeError?.message)
                : undefined
            }
            data-testid="postal-code"
            classNames={{
              inputWrapper: 'border-solid'
            }}
          />
        ) : (
          <Input
            {...register('email')}
            type="email"
            autoComplete="email"
            label={translate('landing_email')}
            radius="sm"
            size="md"
            variant="bordered"
            isInvalid={!!emailError}
            errorMessage={
              emailError?.message ? translate(emailError?.message) : undefined
            }
            data-testid="email"
            classNames={{
              inputWrapper: 'border-solid'
            }}
          />
        )}
        <Button
          type="submit"
          variant="solid"
          radius="sm"
          size="lg"
          fullWidth
          data-testid="continue"
          style={{
            backgroundColor: color,
            color: textColor,
            borderRadius: '10px'
          }}
          isLoading={isPending}
        >
          <span className="text-[13px] uppercase tracking-wider">
            {translate('landing_button_continue')}
          </span>
        </Button>
      </Form>
    )
  }

  return (
    <Form
      className="w-full max-w-96 space-y-2"
      onSubmit={handleSubmit(handleLogin)}
    >
      <Input
        {...register('orderId')}
        type="text"
        label={translate('landing_numorder')}
        placeholder="X01234"
        radius="sm"
        variant={color === 'transparent' ? 'underlined' : 'bordered'}
        autoFocus
        isInvalid={!!errors.orderId}
        errorMessage={
          errors.orderId?.message
            ? translate(errors.orderId?.message)
            : undefined
        }
        data-testid="orderNumber"
        classNames={{
          inputWrapper: 'border-solid'
        }}
      />
      {identificationFlow ===
      PbPortalSettingsIdentificationFlowEnum.OrderIdAndPostalCode ? (
        <Input
          {...register('postalCode')}
          label={translate('landing_postal_code')}
          placeholder="12345"
          autoComplete="postal-code"
          radius="sm"
          size="md"
          variant={color === 'transparent' ? 'underlined' : 'bordered'}
          isInvalid={!!postalCodeError}
          errorMessage={
            postalCodeError?.message
              ? translate(postalCodeError?.message)
              : undefined
          }
          data-testid="postal-code"
          classNames={{
            inputWrapper: 'border-solid'
          }}
        />
      ) : (
        <Input
          {...register('email')}
          type="email"
          autoComplete="email"
          label={translate('landing_email')}
          placeholder={exampleEmail}
          radius="sm"
          size="md"
          variant={color === 'transparent' ? 'underlined' : 'bordered'}
          isInvalid={!!emailError}
          errorMessage={
            emailError?.message ? translate(emailError?.message) : undefined
          }
          data-testid="email"
          classNames={{
            inputWrapper: 'border-solid'
          }}
        />
      )}
      <Button
        type="submit"
        variant={color === 'transparent' ? 'light' : 'solid'}
        radius="sm"
        size={color === 'transparent' ? 'md' : 'lg'}
        fullWidth
        data-testid="continue"
        className={clsx({
          'underline underline-offset-2 hover:!bg-neutral-100':
            color === 'transparent'
        })}
        style={{
          backgroundColor: color === 'transparent' ? 'transparent' : color,
          color: textColor,
          borderRadius: rounded
        }}
        isLoading={isPending}
        spinner={<Spinner size="sm" />}
      >
        {translate('landing_button_continue')}
      </Button>
    </Form>
  )
}
